import { ChatCircle, Plus, Users } from "@phosphor-icons/react";
import { SegmentedControl } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import { type AskV2ReviewLeftPane } from "./useAskV2ReviewWorkspace";

export function ReviewPaneTabs({
  leftPane,
  commentCount,
  onChange,
  "data-testid": testId = "ask-v2-review-pane-tabs",
}: {
  leftPane: AskV2ReviewLeftPane;
  commentCount: number;
  onChange: (pane: AskV2ReviewLeftPane) => void;
  "data-testid"?: string;
}) {
  const { t } = useTranslation("ui");

  // why: the wrapper below kept `role="tablist"` from the hand-rolled tabs
  // this pane switcher used before it moved to Radix SegmentedControl. Radix
  // renders a single-select ToggleGroup — `role="group"` on the root,
  // `role="radio"` + `aria-checked` on each item (the shape
  // qa/rickrubin/tests/specs/cca/ask.v2.review-workspace.spec.ts already
  // asserts) — so the wrapper claimed a role that owns zero `role="tab"`
  // children, an aria-required-children violation a screen reader reads out
  // as an empty tab list. It also parked the switcher's only accessible name
  // on that bogus node, leaving the real group nameless. The label belongs on
  // the element that actually carries the grouping role.
  return (
    <div data-testid={testId}>
      <SegmentedControl.Root
        value={leftPane}
        radius="full"
        // why: this switcher is a Radix SegmentedControl -- a ToggleGroup
        // type="single", whose items render `role="radio"`, never `role="tab"`.
        // The wrapper used to claim `role="tablist"`, so the review pane
        // switcher was a tab list owning zero tabs (axe
        // aria-required-children), and the one accessible name it has sat on
        // that wrapper instead of on the control a reader operates -- the
        // radios were announced with no group name and no "1 of 2" position.
        // AskV2ProjectTabs is the real tablist-with-tabs case; this is a radio
        // group, so name it as one and let the radios be owned by it.
        role="radiogroup"
        aria-label={t("askV2.review.paneTabsLabel")}
        onValueChange={(value) => {
          if (value === "feed" || value === "comments") {
            onChange(value);
          }
        }}
      >
        <SegmentedControl.Item
          value="feed"
          data-testid="ask-v2-review-tab-feed"
        >
          <span className="flex items-center gap-1.5">
            <Users size={16} aria-hidden="true" />
            {t("askV2.review.feedTab")}
          </span>
        </SegmentedControl.Item>
        <SegmentedControl.Item
          value="comments"
          data-testid="ask-v2-review-tab-comments"
        >
          <span className="flex items-center gap-1.5">
            <span className="relative inline-flex shrink-0" aria-hidden="true">
              <ChatCircle size={16} />
              <Plus
                size={8}
                weight="bold"
                className="absolute -bottom-0.5 -right-0.5"
              />
            </span>
            {t("askV2.review.commentsTab", { count: commentCount })}
          </span>
        </SegmentedControl.Item>
      </SegmentedControl.Root>
    </div>
  );
}
